import React, { useState } from 'react';
import { FlatList, RefreshControl, TouchableOpacity, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { Image } from 'expo-image';
import { Ionicons } from '@expo/vector-icons';

import api from '../../../../utils/api';
import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import CameraPlayerModal, { cameraProblem } from './CameraPlayerModal';

// Las miniaturas vienen del equipo de la caseta con un token corto: al refrescar la lista
// llegan URLs nuevas y con ellas la imagen más reciente.
const REFRESH_MS = 30_000;

function CameraTile({ camera, onPress }) {
  const online = camera.is_online;
  const place = camera.access_point?.name ?? camera.zone?.name;

  return (
    <TouchableOpacity style={tw`flex-1 m-1.5`} onPress={onPress} activeOpacity={0.85}>
      {/* Preview */}
      <View style={tw`aspect-video rounded-xl bg-gray-800 overflow-hidden justify-center items-center`}>
        {camera.thumb_url ? (
          <Image
            source={{ uri: camera.thumb_url }}
            style={tw`w-full h-full`}
            contentFit="cover"
            transition={150}
          />
        ) : (
          <Ionicons name="videocam-outline" size={32} color={tw.color('gray-500')} />
        )}

        {/* Overlay play */}
        <View style={tw`absolute top-0 left-0 right-0 bottom-0 items-center justify-center`}>
          <View style={tw`bg-black/35 rounded-full p-2`}>
            <Ionicons name="play" size={20} color="white" />
          </View>
        </View>

        {/* Badge online */}
        <View style={tw`absolute top-2 left-2 row items-center bg-black/45 rounded-full px-2 py-0.5`}>
          <View style={tw.style(`h-2 w-2 rounded-full mr-1`, online ? `bg-primary` : `bg-gray-400`)} />
          <Text style={tw`text-white text-xs`}>{online ? 'En vivo' : cameraProblem(camera.status, true)}</Text>
        </View>
      </View>

      {/* Nombre + gate */}
      <View style={tw`px-1 pt-1.5`}>
        <Text style={tw`text-secondary`} numberOfLines={1}>{camera.name}</Text>
        {place && (
          <Text style={tw`text-gray-400 text-xs`} numberOfLines={1}>{place}</Text>
        )}
      </View>
    </TouchableOpacity>
  );
}

export default function CamerasGrid({ communityId, numColumns = 2 }) {
  const [selectedCamera, setSelectedCamera] = useState(null);

  const { data: cameras, refetch, isLoading } = useQuery({
    queryKey: ['community.cameras', communityId],
    queryFn: () =>
      api.get(`/communities/${communityId}/sentinel/cameras`).then(response => response.data.data),
    refetchInterval: REFRESH_MS,
  });

  return (
    <View style={tw`flex-1`}>
      <FlatList
        key={`cols-${numColumns}`}
        data={cameras}
        numColumns={numColumns}
        contentContainerStyle={tw`px-1.5 pb-6`}
        refreshControl={<RefreshControl onRefresh={refetch} refreshing={isLoading} />}
        renderItem={({ item: camera }) => (
          <CameraTile camera={camera} onPress={() => setSelectedCamera(camera)} />
        )}
        keyExtractor={item => item.uuid}
        ListEmptyComponent={
          isLoading ? null : (
            <View style={tw`items-center justify-center mt-20 px-8`}>
              <Ionicons name="videocam-off-outline" size={48} color={tw.color('gray-300')} />
              <Text style={tw`text-gray-400 mt-3 text-center`}>
                Aún no hay cámaras configuradas
              </Text>
            </View>
          )
        }
      />

      {selectedCamera && (
        <CameraPlayerModal
          communityId={communityId}
          camera={selectedCamera}
          onRequestClose={() => setSelectedCamera(null)}
        />
      )}
    </View>
  );
}
